import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import NavBar from '../../components/Navbar/NavBar';
import Footer from '../../components/Footer';
import GalleryComponent from '../../components/Gallery';
import SideMenu from '../../components/SideMenu';
import AudioPlayer from '../../components/AudioPlayer';
import ContactUs from '../../components/ContactUs';
import { useLanguage } from '../../context/LanguageContext';
import axios from 'axios';

const AudioDescriptionDetails = () => {
  const { programId } = useParams();
  const { language } = useLanguage();
  const [program, setProgram] = useState(null);
  const [menuItems, setMenuItems] = useState([]);

  useEffect(() => {
    const fetchProgramDetails = async () => {
      try {
        const response = await axios.get(`https://annexpilot.com/dashboard/wp-json/wp/v2/audiodescriptions/${programId}`);
        const data = response.data;

        setProgram(data);

        // Generate menu items dynamically
        const dynamicMenuItems = [
          { href: '#overview', label: language === 'English' ? 'Overview' : 'Përmbledhje' },
        ];

        // Extract headings for the "Synopsis" subitems
        const synopsisSubitems = extractHeadings(data.synopsis).map((heading) => ({
          href: `#${heading.id}`,
          label: heading.text,
        }));

        // Add "Synopsis" with subitems
        dynamicMenuItems.splice(1, 0, {
          href: '#synopsis',
          label: language === 'English' ? 'Synopsis' : 'Sinopsi',
          subitems: synopsisSubitems,
        });

        // Add other sections
        if (data.compositor_description || data.compositor_name) {
          dynamicMenuItems.push({
            href: '#compositor',
            label: language === 'English' ? 'Compositor' : 'Kompozitori',
          });
        }

        if (data.creators) {
          dynamicMenuItems.push({
            href: '#creators',
            label: language === 'English' ? 'Creators' : 'Krijuesit',
          });
        }

        if (data.creators_and_performers) {
          dynamicMenuItems.push({
            href: '#characters-performers',
            label: language === 'English' ? 'Characters and Performers' : 'Personazhet dhe Performuesit',
          });
        }

        if (data.gallery_image && data.gallery_image.length > 0) {
          dynamicMenuItems.push({
            href: '#gallery',
            label: language === 'English' ? 'Gallery' : 'Galeria',
          });
        }

        setMenuItems(dynamicMenuItems);
      } catch (error) {
        console.error('Error fetching program details:', error);
      }
    };

    fetchProgramDetails();
  }, [programId, language]);

  const extractHeadings = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const headings = Array.from(doc.querySelectorAll('h1, h2')).map((heading, index) => ({
      id: `heading-${index}`,
      text: heading.textContent,
    }));
    return headings;
  };

  const processSynopsisWithAudioFiles = (synopsis, audioFiles) => {
    const parts = synopsis.split('[audio_files]');
    return parts.reduce((acc, part, index) => {
      acc.push(
        <div key={`text-${index}`} dangerouslySetInnerHTML={{ __html: part }} className="mb-4 text-gray-700" />
      );
      if (index < audioFiles.length) {
        acc.push(
          <AudioPlayer
            key={`audio-${index}`}
            src={audioFiles[index]?.guid}
            aria-label={
              language === 'English'
                ? `Audio file ${audioFiles[index]?.post_title}`
                : `Skedar audio ${audioFiles[index]?.post_title}`
            }
          />
        );
      }
      return acc;
    }, []);
  };

  if (!program) {
    return <div>{language === 'English' ? 'Program not found' : 'Programi nuk u gjet'}</div>;
  }

  return (
    <>
      <NavBar />
      <div className="flex min-h-screen">
        <aside
          className="hidden md:block w-1/4 bg-gray-100 px-8 py-12 sticky top-16 h-screen"
          aria-label={language === 'English' ? 'Sidebar navigation' : 'Navigimi anësor'}
        >
          <SideMenu
            backLink="/artistic-program/audio-descriptions"
            backText={language === 'English' ? 'Audio Descriptions' : 'Përshkrimet Zanore'}
            menuItems={menuItems}
          />
        </aside>

        <main className="w-full md:w-3/4 p-6 md:p-12 bg-white">
          <section id="overview" className="mb-12">
            {program.poster_image && (
              <div className="mb-6">
                <img
                  src={program.poster_image.guid}
                  alt={language === 'English' ? 'Main poster image' : 'Imazhi kryesor i posterit'}
                  className="w-full h-auto rounded-lg shadow-md"
                />
              </div>
            )}
            <h1 className="text-5xl font-bold mb-6">{program.audio_description_title}</h1>
            <p className="text-lg text-gray-700 leading-relaxed">{program.audio_description_subtitle}</p>
          </section>

          <section id="synopsis" className="mb-12">
            <h2 className="text-3xl font-semibold mb-4">{language === 'English' ? 'Synopsis' : 'Sinopsi'}</h2>
            {processSynopsisWithAudioFiles(program.synopsis, program.audio_files)}
          </section>

          <section id="compositor" className="mb-12">
            <h2 className="text-3xl font-semibold mb-4">{language === 'English' ? 'Compositor' : 'Kompozitori'}</h2>
            {program.compositor_name && <p className="font-bold text-lg mb-4">{program.compositor_name}</p>}
            <div dangerouslySetInnerHTML={{ __html: program.compositor_description }} />
          </section>

          <section id="creators" className="mb-12">
            <h2 className="text-3xl font-semibold mb-4">{language === 'English' ? 'Creators' : 'Krijuesit'}</h2>
            <div>
              {program.creators
                .split('\r\n')
                .map((line, index) => (
                  <p key={index} dangerouslySetInnerHTML={{ __html: line }} className="mb-2" />
                ))}
            </div>
          </section>

          <section id="characters-performers" className="mb-12">
            <h2 className="text-3xl font-semibold mb-4">
              {language === 'English' ? 'Characters and Performers' : 'Personazhet dhe Performuesit'}
            </h2>
            <div>
              {program.creators_and_performers
                .split('\r\n')
                .map((line, index) => (
                  <p key={index} dangerouslySetInnerHTML={{ __html: line }} className="mb-2" />
                ))}
            </div>
          </section>

          {program.gallery_image && program.gallery_image.length > 0 && (
            <section id="gallery" className="mb-12">
              <h2 className="text-3xl font-semibold mb-4">{language === 'English' ? 'Gallery' : 'Galeria'}</h2>
              <GalleryComponent
                images={program.gallery_image.map((image) => ({
                  src: image.guid,
                  description: image.post_content,
                  alt: image.post_excerpt,
                }))}
              />
            </section>
          )}

          <ContactUs />
        </main>
      </div>
      <Footer />
    </>
  );
};

export default AudioDescriptionDetails;